import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useDashboardTemplates from '../hooks/useDashboardTemplates'
import useGenerateDashboardStructure, { GENERATED_CONFIG_STORAGE_KEY } from '../hooks/useGenerateDashboardStructure'
import useAiAccess from '../hooks/useAiAccess'
import PageHeader from '../components/system/PageHeader'
import TemplateLibrary, { TemplateChoice } from '../components/dashboard/builder/TemplateLibrary'
import AgentCreatePanel from '../components/dashboard/builder/AgentCreatePanel'
import { MultiTableConfigTable } from '../types/gotypes'
import { tableFullPath } from '../components/dashboard/widgets/genai/wizardHelpers'

type Step = 'entry' | 'templates' | 'name' | 'agent'

interface EntryCardDef {
  key: 'build' | 'template' | 'agent'
  icon: React.ReactNode
  title: string
  description: string
  disabled?: boolean
  badge?: string
}

const ENTRY_CARDS: EntryCardDef[] = [
  {
    key: 'build',
    icon: <ViewQuiltIcon sx={{ fontSize: 32 }} />,
    title: 'Build it yourself',
    description: 'Drag and drop your layout, then fill in your data.',
  },
  {
    key: 'template',
    icon: <DashboardCustomizeIcon sx={{ fontSize: 32 }} />,
    title: 'Start from a template',
    description: 'Choose a layout to start from — system, company or personal.',
  },
  {
    key: 'agent',
    icon: <SmartToyIcon sx={{ fontSize: 32 }} />,
    title: 'Let the agent help',
    description: 'Describe what you want or select your data sources — the agent builds the structure.',
  },
]

/**
 * The "+ New Dashboard" landing screen (#971 Phase 1). Three entry points
 * converging on the layout builder — one destination, three on-ramps. The
 * agent-assisted path (#971 Phase 3) is a describe step + one structured LLM
 * call (no agent session — decision on #1048): the returned structure-only
 * config seeds the layout builder exactly like a template does.
 */
const DashboardCreatePage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const customer = router.params.customer || account.customer?.name || ''
  const job = account.job?.name || ''
  const userEmail = account.user?.session_info?.email || ''

  const [step, setStep] = useState<Step>('entry')
  const [pendingTemplate, setPendingTemplate] = useState<TemplateChoice | null>(null)
  const [name, setName] = useState('')
  // #1182: every dashboard starts with Page 1 auto-created, and the user is
  // prompted to name it here, before they continue. Blank keeps "Page 1".
  // Only the blank-canvas path asks — templates bring their own named pages.
  const [pageName, setPageName] = useState('')

  // Agent-assisted creation (#971 Phase 3) — describe-step inputs live here
  // so a failed generation keeps everything the user typed (retryable, never
  // a dead end). Table selection is optional; the agent only ever references
  // tables offered through it.
  const [agentDescription, setAgentDescription] = useState('')
  const [agentTables, setAgentTables] = useState<MultiTableConfigTable[]>([])
  const { generate, isGenerating, error: generateError } = useGenerateDashboardStructure({ customer })
  // AI access (#1537): generating a structure needs level 2.
  const aiAccess = useAiAccess(customer)

  // Personal + company templates (#971 Phase 2) — fetched here at page level
  // (Page -> Hook -> Component) and handed down to TemplateLibrary as props.
  const { templates, templatesLoading } = useDashboardTemplates({ customer })
  const { companyTemplates, personalTemplates } = useMemo(() => {
    const companyTemplates = templates.filter(t => Boolean(t.promoted_at))
    const personalTemplates = templates.filter(t => !t.promoted_at && t.user === userEmail)
    return { companyTemplates, personalTemplates }
  }, [templates, userEmail])

  const handleBack = () => {
    if (step === 'name') {
      setStep(pendingTemplate ? 'templates' : 'entry')
      return
    }
    if (step === 'templates' || step === 'agent') {
      setStep('entry')
      return
    }
    router.navigate('dashboards', { customer })
  }

  const handleEntryClick = (key: EntryCardDef['key']) => {
    if (key === 'build') {
      setPendingTemplate(null)
      setStep('name')
    } else if (key === 'template') {
      setStep('templates')
    } else if (key === 'agent') {
      setPendingTemplate(null)
      setStep('agent')
    }
  }

  const handleAgentGenerate = async () => {
    const config = await generate({
      description: agentDescription.trim(),
      job,
      source_tables: agentTables.map(t => ({ name: t.displayName, path: tableFullPath(t) })),
    })
    if (!config) return // error state renders inline; inputs stay put for retry
    // A whole config can't ride a query param the way a template id does —
    // stash it and land in the builder with the same navigation handoff the
    // template paths use (see GENERATED_CONFIG_STORAGE_KEY's doc comment).
    sessionStorage.setItem(GENERATED_CONFIG_STORAGE_KEY, JSON.stringify(config))
    router.navigate('dashboard-builder', { customer, name: name.trim(), generated: '1' })
  }

  const handleTemplateSelect = (choice: TemplateChoice) => {
    setPendingTemplate(choice)
    setName(choice.kind === 'system' ? choice.template.name : (choice.dashboard.config?.name || 'Untitled template'))
    setStep('name')
  }

  const pendingTemplateName = pendingTemplate
    ? (pendingTemplate.kind === 'system' ? pendingTemplate.template.name : (pendingTemplate.dashboard.config?.name || 'Untitled template'))
    : null

  const handleContinue = () => {
    if (!name.trim()) return
    const templateParams = !pendingTemplate
      ? {}
      : pendingTemplate.kind === 'system'
        ? { template: pendingTemplate.template.id }
        : { templateDashboardId: pendingTemplate.dashboard.id }
    router.navigate('dashboard-builder', {
      customer,
      name: name.trim(),
      ...(!pendingTemplate && pageName.trim() ? { pageName: pageName.trim() } : {}),
      ...templateParams,
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <PageHeader
        title="New Dashboard"
        subtitle={job ? `${account.customer?.displayName || customer} — ${job}` : account.customer?.displayName || customer}
        actions={<Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">Back</Button>}
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {!job ? (
          <Alert severity="info">Open a job in your session before creating a dashboard.</Alert>
        ) : step === 'entry' ? (
          <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto', mt: 2 }}>
            {ENTRY_CARDS.map(card => (
              <Grid item xs={12} md={4} key={card.key}>
                <Card variant="outlined" sx={{ height: '100%', opacity: card.disabled ? 0.6 : 1 }}>
                  <CardActionArea
                    disabled={card.disabled}
                    onClick={() => handleEntryClick(card.key)}
                    sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}
                  >
                    <Box sx={{ color: 'info.main' }}>{card.icon}</Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{card.title}</Typography>
                      {card.badge && <Chip label={card.badge} size="small" color="default" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{card.description}</Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : step === 'agent' ? (
          <AgentCreatePanel
            customer={customer}
            job={job}
            name={name}
            onNameChange={setName}
            description={agentDescription}
            onDescriptionChange={setAgentDescription}
            selectedTables={agentTables}
            onSelectedTablesChange={setAgentTables}
            isGenerating={isGenerating}
            error={generateError}
            onGenerate={handleAgentGenerate}
            blockedReason={aiAccess.can('dashboard_generate') ? undefined : aiAccess.refusalFor('dashboard_generate')}
            onRequestAccess={aiAccess.levelAllows('dashboard_generate') ? undefined : () => router.navigate('ai-access', { customer })}
          />
        ) : step === 'templates' ? (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Start from a template</Typography>
            <TemplateLibrary
              job={job}
              customerDisplayName={account.customer?.displayName || customer}
              companyTemplates={companyTemplates}
              personalTemplates={personalTemplates}
              templatesLoading={templatesLoading}
              onSelect={handleTemplateSelect}
            />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Name your dashboard</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {pendingTemplateName ? `Starting from "${pendingTemplateName}".` : 'Starting from a blank canvas.'}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Dashboard name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleContinue() }}
              sx={{ mb: 2 }}
            />
            {/* #1182: the dashboard opens with its first page already created —
                name it now. Templates skip this: they bring their own pages. */}
            {!pendingTemplate && (
              <TextField
                fullWidth
                label="First page name"
                placeholder="Page 1"
                value={pageName}
                onChange={(e) => setPageName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleContinue() }}
                sx={{ mb: 2 }}
              />
            )}
            <Button variant="contained" color="info" onClick={handleContinue} disabled={!name.trim()}>
              Continue to layout builder
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default DashboardCreatePage
